/** Device-local storage for keys, the decrypted message cache and the outbox. */
export interface Store {
  get<T>(key: string): Promise<T | undefined>;
  set(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
  keys(prefix: string): Promise<string[]>;
}

export class MemoryStore implements Store {
  private data = new Map<string, unknown>();

  async get<T>(key: string) {
    return structuredClone(this.data.get(key)) as T | undefined;
  }
  async set(key: string, value: unknown) {
    this.data.set(key, structuredClone(value));
  }
  async delete(key: string) {
    this.data.delete(key);
  }
  async keys(prefix: string) {
    return [...this.data.keys()].filter((k) => k.startsWith(prefix));
  }
}

/** IndexedDB, one database per signed-in user so accounts on a shared device stay apart. */
export class IdbStore implements Store {
  private db: Promise<IDBDatabase>;

  constructor(name: string) {
    this.db = new Promise((resolve, reject) => {
      const req = indexedDB.open(name, 1);
      req.onupgradeneeded = () => req.result.createObjectStore("kv");
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  private async run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
    const db = await this.db;
    return new Promise((resolve, reject) => {
      const req = fn(db.transaction("kv", mode).objectStore("kv"));
      req.onsuccess = () => resolve(req.result as T);
      req.onerror = () => reject(req.error);
    });
  }

  get<T>(key: string) {
    return this.run<T | undefined>("readonly", (s) => s.get(key));
  }
  async set(key: string, value: unknown) {
    await this.run("readwrite", (s) => s.put(value, key));
  }
  async delete(key: string) {
    await this.run("readwrite", (s) => s.delete(key));
  }
  async keys(prefix: string) {
    const range = IDBKeyRange.bound(prefix, `${prefix}￿`);
    return this.run<string[]>("readonly", (s) => s.getAllKeys(range));
  }

  /** Whether a database of this name is on the device. Null when the browser cannot say. */
  static async exists(name: string): Promise<boolean | null> {
    if (typeof indexedDB === "undefined" || typeof indexedDB.databases !== "function") return null;
    try {
      return (await indexedDB.databases()).some((d) => d.name === name);
    } catch {
      return null;
    }
  }

  static async destroy(name: string): Promise<void> {
    await new Promise<void>((resolve) => {
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = req.onerror = req.onblocked = () => resolve();
    });
  }
}
